import { useState, useEffect } from 'react'
import Button from '@/components/Button'
import { PageMetadata } from '@/types/page'
import { useHighlight } from '@/hooks/useHighlight'
import { ThemeColors } from './shared/types'
import { applyCustomization, generateCode } from './shared/widgetState'
import { WidgetPreview } from './shared/WidgetPreview'
import { CodeOutput } from './shared/CodeOutput'

export const metadata: PageMetadata = {
  id: 'customization-1-colors',
  category: 'customization',
  categoryName: 'Customization',
  name: 'Theme & Colors',
  icon: '🎨',
  path: '/customization/colors',
  title: 'Theme & Colors',
  description: 'Customize widget colors and themes'
}

const PRESETS = {
  red: {
    primary: '#B71C1C',
    onPrimary: '#FFFFFF',
    message: '#FFCDD2',
    onMessage: '#3E0000',
    action: '#D32F2F',
    onAction: '#FFFFFF',
    background: '#FFF3F3',
    onBackground: '#3E0000',
    conversationListBackground: '#FFEBEE',
    onConversationListBackground: '#3E0000'
  },
  blue: {
    primary: '#2E7D32',
    onPrimary: '#FFFFFF',
    message: '#C8E6C9',
    onMessage: '#1B5E20',
    action: '#43A047',
    onAction: '#FFFFFF',
    background: '#F1F8F4',
    onBackground: '#1B5E20',
    conversationListBackground: '#E8F5E9',
    onConversationListBackground: '#1B5E20'
  },
  old: {
    primary: '#0D47A1',
    onPrimary: '#FFFFFF',
    message: '#BBDEFB',
    onMessage: '#002171',
    action: '#1976D2',
    onAction: '#FFFFFF',
    background: '#F3F8FF',
    onBackground: '#002171',
    conversationListBackground: '#E3F2FD',
    onConversationListBackground: '#002171'
  },
  internalnote: {
    primary: '#006DFF',
    onPrimary: '#FFFFFF',
    message: '#BBDEFB',
    onMessage: '#002171',
    action: '#1976D2',
    onAction: '#FFFFFF',
    background: '#fefaf2',
    onBackground: '#002171',
    conversationListBackground: '#E3F2FD',
    onConversationListBackground: '#002171'
  }
}

export default function Colors() {
  const [theme, setTheme] = useState<ThemeColors>(PRESETS.old)
  const [viewMode, setViewMode] = useState<'configure' | 'code'>('configure')

  const codeRef = useHighlight([theme, viewMode])

  useEffect(() => {
    document.title = `Internal Note - ${metadata.title}`

    // Configure Zendesk messenger
    const zendeskConfig = document.createElement('script')
    zendeskConfig.innerHTML = `
      window.zEMessenger = {
        autorender: false,
      }
    `
    document.head.appendChild(zendeskConfig)

    // Add Zendesk widget script
    const script = document.createElement('script')
    script.id = 'ze-snippet'
    script.src = 'https://static.zdassets.com/ekr/snippet.js?key=e125418a-9466-44d8-9b3f-2ac10e911ea4'
    script.async = true
    document.body.appendChild(script)

    // Load highlight.js for syntax highlighting
    const highlightScript = document.createElement('script')
    highlightScript.src = 'https://cdnjs.cloudflare.com/ajax/libs/highlight.js/11.5.1/highlight.min.js'
    highlightScript.async = true
    document.body.appendChild(highlightScript)

    highlightScript.onload = () => {
      if (window.hljs) {
        window.hljs.highlightAll()
      }
    }

    // Wait for Zendesk to load then render embedded widget
    script.onload = () => {
      if (window.zE) {
        window.zE('messenger', 'render', {
          mode: 'embedded',
          widget: {
            targetElement: '#widget',
          }
        })

        // Open messenger widget after rendering
        setTimeout(() => {
          if (window.zE) {
            window.zE('messenger', 'open')
          }
        }, 500)
      }
    }

    // Apply default theme
    applyCustomization({ theme: PRESETS.old })

    return () => {
      const existing = document.getElementById('ze-snippet')
      if (existing) existing.remove()
      if (zendeskConfig.parentNode) {
        document.head.removeChild(zendeskConfig)
      }
      if (highlightScript.parentNode) {
        highlightScript.remove()
      }
    }
  }, [])

  const handleThemeChange = (field: keyof ThemeColors, value: string) => {
    const newTheme = { ...theme, [field]: value }
    setTheme(newTheme)
    applyCustomization({ theme: newTheme })
  }

  const handlePreset = (presetName: keyof typeof PRESETS) => {
    const preset = PRESETS[presetName]
    setTheme(preset)
    applyCustomization({ theme: preset })
  }

  const handleRandomTheme = () => {
    const randomColor = () => '#' + Math.floor(Math.random() * 16777215).toString(16).padStart(6, '0')
    const randomTheme: ThemeColors = {
      primary: randomColor(),
      onPrimary: '#FFFFFF',
      message: randomColor(),
      onMessage: '#000000',
      businessMessage: '#FFFFFF',
      onBusinessMessage: '#000000',
      action: randomColor(),
      onAction: '#FFFFFF',
      background: randomColor(),
      onBackground: '#000000',
      error: '#FF1744',
      onError: '#FFFFFF',
      notify: randomColor(),
      onNotify: '#FFFFFF',
      onSecondaryAction: '#000000',
      conversationListBackground: randomColor(),
      onConversationListBackground: '#000000'
    }
    setTheme(randomTheme)
    applyCustomization({ theme: randomTheme })
  }

  const handleReset = () => {
    setTheme(PRESETS.old)
    applyCustomization({ theme: PRESETS.old })
  }

  const handleCopyCode = async () => {
    const code = generateCode({ theme })
    try {
      await navigator.clipboard.writeText(code)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  return (
    <>
      <header>
        <div className="py-6 px-4 sm:px-6 md:px-8 mt-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl text-licorice dark:text-white">{metadata.title}</h1>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                Customize widget theme colors and apply preset themes
              </p>
            </div>
            <div className="flex gap-3">
              <Button
                as="a"
                href="https://internalnote.com/custom-widget-themes?utm_source=widget_demo&campaign=demo"
                target="_blank"
                rel="noopener noreferrer"
              >
                Show article
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="py-6 px-4 sm:px-6 lg:px-8 mb-32">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-6 items-start">
          {/* Left column: Form - Scrollable */}
          <div className="max-h-[calc(100vh-200px)] overflow-y-auto pr-4">
            {/* Toggle and Action Buttons */}
            <section className="mb-4">
              <div className="flex justify-between items-center">
                <nav className="isolate flex divide-x divide-gray-200 rounded-lg bg-white dark:bg-licorice border border-gray-200">
                  <button
                    onClick={() => setViewMode('configure')}
                    className={`px-6 py-2 text-sm font-medium rounded-l-lg ${
                      viewMode === 'configure'
                        ? 'bg-indigo-50 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400'
                        : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700 dark:hover:bg-gray-800'
                    }`}
                  >
                    Configure
                  </button>
                  <button
                    onClick={() => setViewMode('code')}
                    className={`px-6 py-2 text-sm font-medium rounded-r-lg ${
                      viewMode === 'code'
                        ? 'bg-indigo-50 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400'
                        : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700 dark:hover:bg-gray-800'
                    }`}
                  >
                    Code
                  </button>
                </nav>
                <div className="flex gap-3">
                  <Button onClick={handleReset} type="button">
                    Reset
                  </Button>
                  <Button onClick={handleCopyCode} type="button">
                    Copy code
                  </Button>
                </div>
              </div>
            </section>

            {/* Configure View */}
            {viewMode === 'configure' && (
              <>
                {/* Theme presets */}
                <section className="mb-4">
                  <h3 className="text-lg font-semibold mb-3 text-licorice dark:text-white">Theme Presets</h3>
                  <div className="grid grid-cols-4 gap-2">
                    <Button onClick={() => handlePreset('red')} type="button">
                      Red theme
                    </Button>
                    <Button onClick={() => handlePreset('blue')} type="button">
                      Green theme
                    </Button>
                    <Button onClick={() => handlePreset('internalnote')} type="button">
                      Blue theme
                    </Button>
                    <button
                      onClick={handleRandomTheme}
                      className="inline-flex items-center justify-center rounded-md border border-purple-600 bg-white dark:bg-licorice px-4 py-2 text-sm font-medium text-purple-600 hover:bg-purple-50"
                      type="button"
                    >
                      Random Theme
                    </button>
                  </div>
                </section>

                {/* Color picker */}
                <section className="mb-4">
                  <h3 className="text-lg font-semibold mb-3 text-licorice dark:text-white">Theme Colors</h3>
                  <form id="theme-form" className="grid gap-3" onSubmit={(e) => e.preventDefault()}>
                    <div className="grid grid-cols-4 gap-2">
                      {['primary', 'onPrimary', 'message', 'onMessage'].map((field) => (
                        <label key={field} className="text-sm text-licorice dark:text-white">
                          {field}
                          <br />
                          <input
                            type="text"
                            value={theme[field as keyof ThemeColors] || ''}
                            onChange={(e) => handleThemeChange(field as keyof ThemeColors, e.target.value)}
                            className="w-full px-2 py-1.5 border rounded-md text-sm text-gray-600"
                            placeholder="#FFFFFF"
                          />
                        </label>
                      ))}
                    </div>

                    <div className="grid grid-cols-4 gap-2">
                      {['businessMessage', 'onBusinessMessage', 'action', 'onAction'].map((field) => (
                        <label key={field} className="text-sm text-licorice dark:text-white">
                          {field}
                          <br />
                          <input
                            type="text"
                            value={theme[field as keyof ThemeColors] || ''}
                            onChange={(e) => handleThemeChange(field as keyof ThemeColors, e.target.value)}
                            className="w-full px-2 py-1.5 border rounded-md text-sm text-gray-600"
                            placeholder="#FFFFFF"
                          />
                        </label>
                      ))}
                    </div>

                    <div className="grid grid-cols-4 gap-2">
                      {['background', 'onBackground', 'error', 'onError'].map((field) => (
                        <label key={field} className="text-sm text-licorice dark:text-white">
                          {field}
                          <br />
                          <input
                            type="text"
                            value={theme[field as keyof ThemeColors] || ''}
                            onChange={(e) => handleThemeChange(field as keyof ThemeColors, e.target.value)}
                            className="w-full px-2 py-1.5 border rounded-md text-sm text-gray-600"
                            placeholder="#FFFFFF"
                          />
                        </label>
                      ))}
                    </div>

                    <div className="grid grid-cols-4 gap-2">
                      {['notify', 'onNotify', 'onSecondaryAction'].map((field) => (
                        <label key={field} className="text-sm text-licorice dark:text-white">
                          {field}
                          <br />
                          <input
                            type="text"
                            value={theme[field as keyof ThemeColors] || ''}
                            onChange={(e) => handleThemeChange(field as keyof ThemeColors, e.target.value)}
                            className="w-full px-2 py-1.5 border rounded-md text-sm text-gray-600"
                            placeholder="#FFFFFF"
                          />
                        </label>
                      ))}
                    </div>

                    <div className="grid grid-cols-4 gap-2">
                      {['conversationListBackground', 'onConversationListBackground'].map((field) => (
                        <label key={field} className="text-sm text-licorice dark:text-white">
                          {field}
                          <br />
                          <input
                            type="text"
                            value={theme[field as keyof ThemeColors] || ''}
                            onChange={(e) => handleThemeChange(field as keyof ThemeColors, e.target.value)}
                            className="w-full px-2 py-1.5 border rounded-md text-sm text-gray-600"
                            placeholder="#FFFFFF"
                          />
                        </label>
                      ))}
                    </div>
                  </form>
                </section>
              </>
            )}

            {/* Code View */}
            {viewMode === 'code' && (
              <CodeOutput code={generateCode({ theme })} codeRef={codeRef} onCopy={handleCopyCode} />
            )}
          </div>

          {/* Right column: Preview - Sticky */}
          <WidgetPreview />
        </div>
      </div>
    </>
  )
}
